'use client';

import { useEffect, useState } from 'react';
import { toApiKey } from '@polymarket/bindings';
import { createSecureClient, type SecureClient } from '@polymarket/client';
import { signerFrom } from '@polymarket/client/viem';

import { useAuth } from '@/hooks/useAuth';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import { normalizeClobAddress } from '@/utils/clob.utils';

/**
 * useClobClient
 *
 * Provides an authenticated @polymarket/client SecureClient for CLOB trading.
 * This replaces the old clob-client-v2 `ClobClient`. The single SecureClient is
 * built from the user's Privy-backed viem wallet (the order signer), the stored
 * L2 API credentials, and the proxy/deposit wallet as the account/funder.
 *
 * POLY_1271 (deposit-wallet) vs Gnosis-Safe signing is now auto-detected by the
 * SDK from the `wallet` address — no manual signatureType override is needed.
 *
 * Orders are signed client-side by the user's wallet, so no builder/remote-signing
 * apiKey is required here; builder attribution is applied per-order via `builderCode`
 * in useClobOrder.
 *
 * @returns A SecureClient instance, or null while auth/setup is incomplete.
 */

// Module-level cache so the several components/hooks that call useClobClient()
// share ONE SecureClient (its creation performs network auth) instead of each
// deriving its own. Keyed by the inputs that define the client identity.
let cachedKey: string | null = null;
let cachedClientPromise: Promise<SecureClient> | null = null;

function getSharedSecureClient(
  walletClient: any,
  proxyWalletAddress: string,
  creds: { apiKey: string; secret: string; passphrase: string },
): Promise<SecureClient> {
  const signerAddress = walletClient?.account?.address ?? '';
  const key = `${normalizeClobAddress(proxyWalletAddress)}:${creds.apiKey}:${signerAddress}`;
  if (cachedKey === key && cachedClientPromise) {
    return cachedClientPromise;
  }
  cachedKey = key;
  cachedClientPromise = createSecureClient({
    signer: signerFrom(walletClient),
    wallet: normalizeClobAddress(proxyWalletAddress),
    credentials: {
      key: toApiKey(creds.apiKey),
      secret: creds.secret,
      passphrase: creds.passphrase,
    },
  });
  return cachedClientPromise;
}

export function useClobClient(): SecureClient | null {
  const { user, accessToken } = useAuth();
  const { signer } = usePolymarketClient();

  const proxyWalletAddress = user?.proxyWalletAddress;
  const apiKeyCreds = user?.clobCredentials;

  const [client, setClient] = useState<SecureClient | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!accessToken || !proxyWalletAddress || !signer || !apiKeyCreds) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClient(null);
      return;
    }

    getSharedSecureClient(signer, proxyWalletAddress, {
      apiKey: apiKeyCreds.apiKey,
      secret: apiKeyCreds.secret,
      passphrase: apiKeyCreds.passphrase,
    })
      .then((c) => {
        if (!cancelled) setClient(c);
      })
      .catch((err) => {
        console.error('[useClobClient] Failed to create SecureClient:', err);
        if (!cancelled) setClient(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    proxyWalletAddress,
    signer,
    apiKeyCreds?.apiKey,
    apiKeyCreds?.secret,
    apiKeyCreds?.passphrase,
  ]);

  return client;
}
